import { useState, useEffect } from 'react';
import { authService } from '../services/authService';
import { assessmentService } from '../services/assessmentService';

/**
 * Custom hook for authentication
 */
export const useAuth = () => {
  const [authToken, setAuthToken] = useState(authService.getToken());
  const [userInfo, setUserInfo] = useState(authService.getUserInfo());
  const [isLoading, setIsLoading] = useState(true);
  const [assessmentsRemaining, setAssessmentsRemaining] = useState(null);
  const [monthlyLimit, setMonthlyLimit] = useState(10);

  /**
   * Handle authentication code exchange
   */
  const handleAuthCode = async (code) => {
    try {
      setIsLoading(true);
      const { token, userInfo: user } = await authService.exchangeCodeForTokens(code);
      
      // Store auth data
      authService.setAuth(token, user);
      setAuthToken(token);
      setUserInfo(user);

      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);

      // Fetch user usage
      await fetchUserUsage(token);
    } catch (error) {
      console.error('Auth error:', error);
      alert('Authentication failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Fetch user usage information
   */
  const fetchUserUsage = async (token = authToken) => {
    if (!token) return;

    try {
      const data = await assessmentService.getUserUsage(token);
      setAssessmentsRemaining(data.remaining);
      setMonthlyLimit(data.monthlyLimit);
    } catch (error) {
      if (error.message === 'UNAUTHORIZED') {
        logout();
      } else {
        console.error('Error fetching usage:', error);
      }
    }
  };

  /**
   * Login
   */
  const login = () => {
    authService.login();
  };

  /**
   * Logout
   */
  const logout = () => {
    setAuthToken(null);
    setUserInfo(null);
    setAssessmentsRemaining(null);
    authService.logout();
  };

  /**
   * Update assessments remaining
   */
  const updateAssessmentsRemaining = (remaining) => {
    setAssessmentsRemaining(remaining);
  };

  // Check for auth code on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const authCode = urlParams.get('code');

    if (authCode) {
      handleAuthCode(authCode);
    } else {
      setIsLoading(false);
      // Fetch user usage if authenticated
      if (authToken) {
        fetchUserUsage();
      }
    }
  }, []); // Only run on mount

  return {
    authToken,
    userInfo,
    isLoading,
    assessmentsRemaining,
    monthlyLimit,
    login,
    logout,
    fetchUserUsage,
    updateAssessmentsRemaining
  };
};

